import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import BottomNav from "../components/BottomNav";
import { ArrowLeft, Search, MapPin, Users, Wine, User, Star, Award, Trophy, Zap, Flag, CheckCircle, ChevronRight, Navigation } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "../components/ui/dialog";

const BADGE_ICONS = {
  star: Star,
  zap: Zap,
  trophy: Trophy,
  flag: Flag,
  award: Award,
  "check-circle": CheckCircle
};

const DiscoverPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [results, setResults] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // "all", "bartenders", "customers"
  
  // Featured bartenders
  const [featuredBartenders, setFeaturedBartenders] = useState([]);
  
  // Radius search
  const [showRadiusSearch, setShowRadiusSearch] = useState(false);
  const [radiusSearchAddress, setRadiusSearchAddress] = useState("");
  const [radiusSearchSuggestions, setRadiusSearchSuggestions] = useState([]);
  const [radiusSearchLocation, setRadiusSearchLocation] = useState(null);
  const [radius, setRadius] = useState(50);
  const [nearbyResults, setNearbyResults] = useState([]);
  const [loadingNearby, setLoadingNearby] = useState(false);
  
  // Badge dialog
  const [selectedBadge, setSelectedBadge] = useState(null);

  useEffect(() => {
    fetchResults();
    fetchFeaturedBartenders();
  }, [filter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchResults(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchFeaturedBartenders = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      let url = `${API}/bartenders/featured`;
      
      // If user has home location, use it
      if (user?.home_location?.lat && user?.home_location?.lng) {
        url += `?lat=${user.home_location.lat}&lng=${user.home_location.lng}&radius=50`;
      }
      
      const response = await axios.get(url, { headers });
      setFeaturedBartenders(response.data);
    } catch (e) {
      console.error("Error fetching featured:", e);
    }
  };

  const fetchResults = async (query = "") => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      if (filter === "bartenders") {
        const url = query ? `${API}/bartenders?search=${encodeURIComponent(query)}` : `${API}/bartenders`;
        const response = await axios.get(url, { headers });
        setResults(response.data.map(b => ({ ...b, type: "bartender" })));
      } else if (filter === "customers") {
        const url = query ? `${API}/users?search=${encodeURIComponent(query)}&role=customer` : `${API}/users?role=customer`;
        const response = await axios.get(url, { headers });
        setResults(response.data.map(c => ({ ...c, type: "customer" })));
      } else {
        // Fetch both
        const bartendersUrl = query ? `${API}/bartenders?search=${encodeURIComponent(query)}` : `${API}/bartenders`;
        const customersUrl = query ? `${API}/users?search=${encodeURIComponent(query)}&role=customer` : `${API}/users?role=customer`;
        
        const [bartendersRes, customersRes] = await Promise.all([
          axios.get(bartendersUrl, { headers }),
          axios.get(customersUrl, { headers })
        ]);
        
        const bartenders = bartendersRes.data.map(b => ({ ...b, type: "bartender" }));
        const customers = customersRes.data.map(c => ({ ...c, type: "customer" }));
        
        // Combine and sort by name
        const combined = [...bartenders, ...customers].sort((a, b) => 
          (a.name || "").localeCompare(b.name || "")
        );
        setResults(combined);
      }
    } catch (e) {
      console.error("Error:", e);
    } finally {
      setLoading(false);
    }
  };

  // Radius search functions
  const searchAddress = async (query) => {
    if (!query || query.length < 3) {
      setRadiusSearchSuggestions([]);
      return;
    }
    
    try {
      const service = new window.google.maps.places.AutocompleteService();
      service.getPlacePredictions(
        { input: query, componentRestrictions: { country: 'us' } },
        (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            setRadiusSearchSuggestions(predictions);
          } else {
            setRadiusSearchSuggestions([]);
          }
        }
      );
    } catch (e) {
      console.error("Places API error:", e);
    }
  };

  const selectAddress = async (place) => {
    try {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ placeId: place.place_id }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const location = results[0].geometry.location;
          setRadiusSearchLocation({
            address: place.description,
            lat: location.lat(),
            lng: location.lng()
          });
          setRadiusSearchAddress(place.description);
          setRadiusSearchSuggestions([]);
          
          // Automatically search nearby
          searchNearby(location.lat(), location.lng());
        }
      });
    } catch (e) {
      console.error("Geocoding error:", e);
    }
  };

  const searchNearby = async (lat, lng) => {
    setLoadingNearby(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(
        `${API}/bartenders/nearby?lat=${lat}&lng=${lng}&radius=${radius}`,
        { headers }
      );
      setNearbyResults(response.data);
    } catch (e) {
      console.error("Nearby search error:", e);
    } finally {
      setLoadingNearby(false);
    }
  };

  const getImageUrl = (path) => {
    if (!path) return null;
    return token ? `${API}/files/${path}?auth=${token}` : `${API}/files/${path}`;
  };

  const getInitials = (name) => {
    return name?.split(" ").map(n => n[0]).join("").toUpperCase() || "?";
  };

  const handleProfileClick = (person) => {
    if (person.type === "bartender" || person.role === "bartender") {
      navigate(`/b/${person.username}`);
    } else {
      navigate(`/u/${person.username}`);
    }
  };

  const renderBadges = (badges) => {
    if (!badges || badges.length === 0) return null;
    return (
      <div className="flex gap-1 mt-1">
        {badges.slice(0, 3).map((badge) => {
          const IconComponent = BADGE_ICONS[badge.icon] || Star;
          return (
            <button
              key={badge.id}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedBadge(badge);
              }}
              className="p-1 rounded-full hover:bg-white/10 transition-colors"
              title={badge.name}
            >
              <IconComponent className="w-4 h-4" style={{ color: badge.color }} />
            </button>
          );
        })}
        {badges.length > 3 && (
          <span className="text-white/40 text-xs ml-1">+{badges.length - 3}</span>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background pb-safe" data-testid="discover-page">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-background/80 backdrop-blur-md border-b border-white/5 p-4">
        <div className="flex items-center gap-3 mb-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate(-1)}
            className="text-white/60 hover:text-white"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-lg font-semibold text-white">Discover People</h1>
        </div>
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or username..."
            className="input-dark pl-10"
            data-testid="search-input"
          />
        </div>
        {/* Filter Tabs */}
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setFilter("all")}
            className={`px-4 py-2 rounded-full text-sm transition-colors ${
              filter === "all" 
                ? "bg-primary text-white" 
                : "bg-white/5 text-white/60 hover:bg-white/10"
            }`}
            data-testid="filter-all"
          >
            All
          </button>
          <button
            onClick={() => setFilter("bartenders")}
            className={`px-4 py-2 rounded-full text-sm transition-colors flex items-center gap-1 ${
              filter === "bartenders" 
                ? "bg-primary text-white" 
                : "bg-white/5 text-white/60 hover:bg-white/10"
            }`}
            data-testid="filter-bartenders"
          >
            <Wine className="w-3 h-3" />
            Bartenders
          </button>
          <button
            onClick={() => setFilter("customers")}
            className={`px-4 py-2 rounded-full text-sm transition-colors flex items-center gap-1 ${
              filter === "customers" 
                ? "bg-primary text-white" 
                : "bg-white/5 text-white/60 hover:bg-white/10"
            }`}
            data-testid="filter-customers"
          >
            <User className="w-3 h-3" />
            Bar-Goers
          </button>
          <button
            onClick={() => setShowRadiusSearch(!showRadiusSearch)}
            className={`px-4 py-2 rounded-full text-sm transition-colors flex items-center gap-1 ${
              showRadiusSearch 
                ? "bg-secondary text-white" 
                : "bg-white/5 text-white/60 hover:bg-white/10"
            }`}
            data-testid="filter-nearby"
          >
            <Navigation className="w-3 h-3" />
            Near Me
          </button>
        </div>
      </header>

      <main className="p-4 space-y-6">
        {/* Radius Search Panel */}
        {showRadiusSearch && (
          <div className="glass-card p-4 space-y-4">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <MapPin className="w-4 h-4 text-secondary" />
              Find Bartenders Near You
            </h3>
            <div className="space-y-2">
              <Input
                value={radiusSearchAddress}
                onChange={(e) => {
                  setRadiusSearchAddress(e.target.value);
                  searchAddress(e.target.value);
                }}
                placeholder="Enter your address or city..."
                className="input-dark"
                data-testid="radius-search-input"
              />
              {radiusSearchSuggestions.length > 0 && (
                <div className="bg-black/50 border border-white/10 rounded-lg overflow-hidden">
                  {radiusSearchSuggestions.map((suggestion) => (
                    <button
                      key={suggestion.place_id}
                      onClick={() => selectAddress(suggestion)}
                      className="w-full text-left p-3 text-white hover:bg-white/10 border-b border-white/5 last:border-0 text-sm"
                    >
                      {suggestion.description}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-3">
              <label className="text-white/60 text-sm">Radius:</label>
              <select
                value={radius}
                onChange={(e) => {
                  setRadius(Number(e.target.value));
                  if (radiusSearchLocation) {
                    searchNearby(radiusSearchLocation.lat, radiusSearchLocation.lng);
                  }
                }}
                className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
              >
                <option value={10}>10 miles</option>
                <option value={20}>20 miles</option>
                <option value={50}>50 miles</option>
                <option value={100}>100 miles</option>
              </select>
            </div>
            
            {/* Nearby Results */}
            {loadingNearby ? (
              <div className="flex justify-center py-8">
                <div className="w-6 h-6 border-2 border-secondary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : nearbyResults.length > 0 ? (
              <div className="space-y-3">
                <p className="text-white/60 text-sm">{nearbyResults.length} bartender{nearbyResults.length > 1 ? 's' : ''} found within {radius} miles</p>
                {nearbyResults.map((person) => (
                  <button
                    key={person.id}
                    onClick={() => handleProfileClick(person)}
                    className="w-full glass-card-hover p-3 flex items-center gap-3 text-left"
                  >
                    <Avatar className="w-12 h-12 border border-white/10">
                      <AvatarImage src={getImageUrl(person.profile_image)} />
                      <AvatarFallback className="bg-primary/20 text-primary">
                        {getInitials(person.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="text-white font-medium truncate">{person.name}</h4>
                        <Wine className="w-3 h-3 text-primary flex-shrink-0" />
                      </div>
                      {renderBadges(person.badges)}
                      <p className="text-white/50 text-xs">
                        {person.distance} mi away · {person.nearest_location?.name}
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-white/30" />
                  </button>
                ))}
              </div>
            ) : radiusSearchLocation ? (
              <p className="text-white/40 text-center py-4">No bartenders found within {radius} miles</p>
            ) : null}
          </div>
        )}

        {/* Featured Bartenders Carousel */}
        {featuredBartenders.length > 0 && !showRadiusSearch && (
          <div>
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
              <Award className="w-5 h-5 text-yellow-500" />
              Featured Bartenders
            </h3>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
              {featuredBartenders.map((person) => (
                <button
                  key={person.id}
                  onClick={() => handleProfileClick(person)}
                  className="flex-shrink-0 w-32 glass-card p-3 text-center hover:bg-white/10 transition-colors"
                >
                  <Avatar className="w-16 h-16 mx-auto border-2 border-yellow-500/50">
                    <AvatarImage src={getImageUrl(person.profile_image)} />
                    <AvatarFallback className="bg-primary/20 text-primary text-xl">
                      {getInitials(person.name)}
                    </AvatarFallback>
                  </Avatar>
                  <p className="text-white font-medium text-sm mt-2 truncate">{person.name}</p>
                  {renderBadges(person.badges)}
                  {person.distance && (
                    <p className="text-white/40 text-xs mt-1">{person.distance} mi</p>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Main Results */}
        {!showRadiusSearch && (
          <>
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : results.length > 0 ? (
              <div className="grid gap-4">
                {results.map((person) => (
                  <button
                    key={person.id}
                    onClick={() => handleProfileClick(person)}
                    className="glass-card-hover p-4 flex items-center gap-4 text-left"
                    data-testid={`person-${person.username}`}
                  >
                    <Avatar className="w-16 h-16 border-2 border-white/10">
                      <AvatarImage src={getImageUrl(person.profile_image)} />
                      <AvatarFallback className={`${
                        person.type === "bartender" ? "bg-primary/20 text-primary" : "bg-secondary/20 text-secondary"
                      } text-xl`}>
                        {getInitials(person.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-white font-semibold text-lg truncate">{person.name}</h3>
                        {person.type === "bartender" ? (
                          <Wine className="w-4 h-4 text-primary flex-shrink-0" />
                        ) : (
                          <User className="w-4 h-4 text-secondary flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-white/60 text-sm">@{person.username}</p>
                      {person.type === "bartender" && renderBadges(person.badges)}
                      <div className="flex items-center gap-4 mt-2">
                        <span className="flex items-center gap-1 text-white/60 text-xs">
                          <Users className="w-3 h-3" />
                          {person.followers?.length || 0} followers
                        </span>
                        {person.type === "bartender" && person.work_locations?.[0] && (
                          <span className="flex items-center gap-1 text-white/60 text-xs">
                            <MapPin className="w-3 h-3" />
                            {person.work_locations[0].name}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <Search className="w-16 h-16 text-white/20 mx-auto mb-4" />
                <p className="text-white/60 mb-2">
                  {search ? "No people found" : "No people yet"}
                </p>
                <p className="text-white/40 text-sm">
                  {search ? "Try a different search term" : "Be the first to join!"}
                </p>
              </div>
            )}
          </>
        )}
      </main>

      {/* Badge Info Dialog */}
      <Dialog open={!!selectedBadge} onOpenChange={() => setSelectedBadge(null)}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-sm">
          {selectedBadge && (
            <>
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-3">
                  {(() => {
                    const IconComponent = BADGE_ICONS[selectedBadge.icon] || Star;
                    return <IconComponent className="w-8 h-8" style={{ color: selectedBadge.color }} />;
                  })()}
                  {selectedBadge.name}
                </DialogTitle>
                <DialogDescription className="text-white/70 leading-relaxed">
                  {selectedBadge.description}
                </DialogDescription>
              </DialogHeader>
            </>
          )}
        </DialogContent>
      </Dialog>

      <BottomNav />
    </div>
  );
};

export default DiscoverPage;
